import { HTMLAttributes, forwardRef, useMemo, useState } from 'react';
import { Accept, DropzoneOptions, FileRejection, useDropzone } from 'react-dropzone';
import { Icon } from '../../icon/icon';
import { FileWithPreview } from '../types';
import { useDevice, useWindowSize } from '@/ui-kit/hooks';
import { cn } from '@/libs/cn';
import './drag-and-drop.css';
import { FieldErrorMessage } from '../../../forms/core';

export interface DragAndDropProps extends HTMLAttributes<HTMLDivElement> {
  onAcceptedFiles?: (files: FileWithPreview[]) => void;
  accept?: Accept;
  maxFiles?: number;
  maxSize?: number;
  includePreview?: boolean;
  name: string;
  onInputChange?: React.ChangeEventHandler<HTMLInputElement>;
  disabled?: boolean;
  hasError?: boolean;
}

const DragAndDrop = forwardRef<HTMLDivElement, DragAndDropProps>(
  (
    {
      onAcceptedFiles,
      accept,
      maxFiles,
      maxSize,
      includePreview,
      name,
      onInputChange,
      disabled,
      hasError,
      ...props
    },
    ref,
  ) => {
    const dimensions = useWindowSize();
    const device = useDevice(dimensions);

    const [validationErrors, setValidationErrors] = useState<string[] | undefined>();

    const DROPZONE_ERROR_MESSAGES: Record<string, string> = useMemo(() => ({
      'too-many-files': `The maximum number of files allowed is ${maxFiles}`,
      'file-too-large': `The maximum file size allowed is ${maxSize! / (1000 * 1000)}MB`
    }), [maxFiles, maxSize]);

    const inputId = `${name}-drag-and-drop`;

    const onDropAccepted: DropzoneOptions['onDropAccepted'] = async (acceptedFiles: File[]) => {
      setValidationErrors(undefined);

      if (!acceptedFiles || !acceptedFiles.length) {
        return;
      }

      const acceptedFilesWithPreview = [...acceptedFiles] as FileWithPreview[];

      if (
        includePreview &&
        (!accept || Object.keys(accept).some(a => a.startsWith('image/')))
      ) {
        await Promise.all(
          acceptedFiles.map(async (file, idx) => {
            if (!file.type.startsWith('image/')) {
              return;
            }

            const preview = await new Promise<string>(resolve => {
              const reader = new FileReader();

              reader.onload = () => {
                resolve(reader.result as string);
              };

              reader.readAsDataURL(file);
            });

            acceptedFilesWithPreview[idx].preview = preview;
          }),
        );
      }

      // Defer until after the native picker closes. UploadFile unmounts this input
      // when uploads start; doing that synchronously on Windows leaves Explorer open.
      setTimeout(() => {
        onAcceptedFiles?.(acceptedFilesWithPreview);
      }, 0);
    };

    const onDropRejected: DropzoneOptions['onDropRejected'] = async (files: FileRejection[]) => {
      if (!files || !files.length) {
        return;
      }

      const uniqueErrors = new Map<string, string>();

      files.forEach((file) => {
        file.errors.forEach((error) => {
          uniqueErrors.set(error.code, error.message);
        })
      });

      const validationErrors = [...uniqueErrors.keys()].map((errorCode) => DROPZONE_ERROR_MESSAGES[errorCode] ?? uniqueErrors.get(errorCode));

      setValidationErrors(validationErrors);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDropAccepted,
      onDropRejected,
      accept,
      maxFiles,
      maxSize,
      multiple: maxFiles ? maxFiles > 1 : true,
      disabled: !!disabled,
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { onChange, name: inputName, ...inputProps } = getInputProps();

    const containerClasses = cn('drag-and-drop', {
      'drag-and-drop-dragging': isDragActive,
      'drag-and-drop--disabled': disabled,
      'drag-and-drop--error': hasError,
    });

    return (
      <>
        <div
          className={containerClasses}
          {...getRootProps({
            onClick: e => {
              if (disabled) {
                e.preventDefault();
                e.stopPropagation();
              }
            },
          })}
          ref={ref}
          {...props}
        >
          <input
            id={inputId}
            {...inputProps}
            disabled={!!disabled}
            onChange={e => {
              onChange?.(e);
              const event = e;
              setTimeout(() => {
                onInputChange?.(event);
                event.target.value = '';
              }, 0);
            }}
            name={name}
          />
          <label
            htmlFor={disabled ? undefined : inputId}
            className="dad-content"
            onClick={e => {
              // Label htmlFor also opens the input; stop bubbling so getRootProps
              // does not call input.click() a second time (Windows keeps Explorer open).
              e.stopPropagation();
              if (disabled) {
                e.preventDefault();
              }
            }}
          >
            <Icon type="upload" size="large" className="dad-icon" />
            <span className="dad-label">
              {isDragActive ? (
                'Release files to upload'
              ) : device === 'desktop' ? (
                <>
                  Drag & Drop <br /> or click here to browse files
                </>
              ) : (
                <>Click here to upload files</>
              )}
            </span>
          </label>
        </div>

        {validationErrors?.map((errorMessage, index) => (
          <FieldErrorMessage key={index} message={errorMessage} />
        ))}
      </>
    );
  },
);

export { DragAndDrop };
